// client/src/components/search/NameSearch.tsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useQuery } from '@tanstack/react-query';
import { createPortal } from 'react-dom';

import type { Suggestion } from '../../types/search/Suggestion';
import { searchEmployeeSuggestions } from '../../services/api';
import LoadingOverlay from '../LoadingOverlay';

interface NameSearchProps {
  setSelectedId?: (id: number) => void;
  setSelectedCode?: (code: string) => void;
  placeholder?: string;
  /** 初回マウント時にプログラムでフォーカス（autoFocus 属性は使わない） */
  focusOnMount?: boolean;
  onSelectedLabel?: (label: string) => void;
  resetSignal?: number;

  /**
   * 並び順を決める比較関数
   * 省略時は「前方一致優先→部分一致→ラベル昇順（ja）」でソート
   * ※ API の順序をそのまま使いたい場合は渡さない
   */
  sortComparator?: (a: Suggestion, b: Suggestion, input: string) => number;
}

const NameSearch = ({
  setSelectedId,
  setSelectedCode,
  placeholder = '氏名を検索',
  focusOnMount = false,
  onSelectedLabel,
  sortComparator,
  resetSignal,
}: NameSearchProps) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isComposing, setIsComposing] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null); // 入力ラッパ
  const inputRef = useRef<HTMLInputElement>(null); // 入力
  const popupRef = useRef<HTMLUListElement>(null); // ポップアップ(Portal)

  useEffect(() => {
    setQuery('');
    onSelectedLabel?.('');
  }, [resetSignal]);

  const {
    data: results = [],
    error,
    isLoading,
    refetch,
  } = useQuery<Suggestion[]>({
    queryKey: ['employee-suggest', query],
    queryFn: () => searchEmployeeSuggestions(query),
    enabled: false, // Enter などで明示実行
  });

  // 初回フォーカス（eslint: no-autofocus 回避）
  useEffect(() => {
    if (focusOnMount) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [focusOnMount]);

  // Portal先も内側扱いにして外側クリックで閉じる
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideInput = containerRef.current?.contains(target) ?? false;
      const insidePopup = popupRef.current?.contains(target) ?? false;
      if (!insideInput && !insidePopup) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  // IME 確定時に検索実行（自動で候補を開く）
  const runSearch = async (currentValue: string) => {
    const q = currentValue.replace(/\s+/g, ''); // 空白除去
    setQuery(q);
    if (!q) return;
    await refetch();
    setOpen(true);
  };

  const commitSelection = (item: Suggestion) => {
    setQuery(item.label);
    setSelectedId?.(item.value);
    setSelectedCode?.(item.code ?? '');
    onSelectedLabel?.(item.label);
    setOpen(false);
    setActiveIndex(-1);
  };

  // デフォルトの並び替え（前方一致→部分一致→日本語昇順）
  const defaultComparator = (a: Suggestion, b: Suggestion, input: string) => {
    const q = input.trim().toLowerCase();
    const la = a.label.toLowerCase();
    const lb = b.label.toLowerCase();

    const score = (l: string) => {
      if (!q) return 2;
      if (l.startsWith(q)) return 0;
      if (l.includes(q)) return 1;
      return 2;
    };

    const sa = score(la);
    const sb = score(lb);
    if (sa !== sb) return sa - sb;

    return a.label.localeCompare(b.label, 'ja');
  };

  // 並べ替えた配列（非破壊）
  const sortedResults = useMemo(() => {
    const arr = results.slice();
    const cmp = (x: Suggestion, y: Suggestion) =>
      (sortComparator ?? defaultComparator)(x, y, query);
    arr.sort(cmp);
    return arr;
  }, [results, sortComparator, query]);

  // 候補が更新されたらハイライト初期化
  useEffect(() => {
    setActiveIndex(sortedResults.length > 0 ? 0 : -1);
  }, [sortedResults]);

  // 入力が空なら閉じる
  useEffect(() => {
    if (!query) {
      setOpen(false);
      setActiveIndex(-1);
    }
  }, [query]);

  // スクロール/リサイズで位置ズレ最小化（軽い再レンダー）
  useEffect(() => {
    const update = () => open && setOpen(true);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  // キー操作（elseなし・早期return）
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // IME 変換中は無視
    const composing =
      isComposing || (e.nativeEvent as any)?.isComposing || e.keyCode === 229;
    if (composing) return;

    switch (e.key) {
      case 'Enter': {
        e.preventDefault();
        if (activeIndex >= 0 && sortedResults.length > 0) {
          commitSelection(sortedResults[activeIndex]);
          return;
        }
        setOpen(true);
        void runSearch(query);
        return;
      }
      case 'ArrowDown': {
        if (sortedResults.length === 0) return;
        e.preventDefault();
        setOpen(true);
        setActiveIndex((prev) => (prev + 1) % sortedResults.length);
        return;
      }
      case 'ArrowUp': {
        if (sortedResults.length === 0) return;
        e.preventDefault();
        setOpen(true);
        setActiveIndex((prev) =>
          prev === -1
            ? sortedResults.length - 1
            : (prev - 1 + sortedResults.length) % sortedResults.length
        );
        return;
      }
      case 'Escape': {
        setOpen(false);
        setActiveIndex(-1);
        return;
      }
      default:
        return;
    }
  };

  return (
    <>
      <LoadingOverlay isLoading={isLoading} isMutating={false} />

      <div className="base-search" ref={containerRef}>
        <Form.Control
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => sortedResults.length > 0 && setOpen(true)}
          onKeyDown={handleKeyDown}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={(e) => {
            setIsComposing(false);
            const v = (e.target as HTMLInputElement).value;
            void runSearch(v);
          }}
          placeholder={placeholder}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={open ? 'name-suggest-list' : undefined}
        />

        {open &&
          sortedResults.length > 0 &&
          containerRef.current &&
          createPortal(
            <ul
              id="name-suggest-list"
              ref={popupRef}
              className="base-search__list list-group shadow-sm"
              role="listbox"
              style={{
                position: 'absolute',
                top:
                  containerRef.current.getBoundingClientRect().bottom +
                  window.scrollY,
                left:
                  containerRef.current.getBoundingClientRect().left +
                  window.scrollX,
                width: containerRef.current.getBoundingClientRect().width,
                zIndex: 1000,
              }}
              onMouseDown={(e) => {
                // ドキュメントの mousedown までバブリングさせない（誤閉じ防止）
                e.stopPropagation();
              }}
            >
              {sortedResults.map((item, index) => {
                const isActive = index === activeIndex;
                return (
                  <li key={item.value} className="list-group-item p-0">
                    <button
                      id={`option-${item.value}`}
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      className={`list-group-item list-group-item-action w-100 text-start py-2 ${
                        isActive ? 'active' : ''
                      }`}
                      onMouseDown={(e) => {
                        // input blur → 閉じる を防ぐ
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => commitSelection(item)}
                    >
                      {item.label}
                    </button>
                  </li>
                );
              })}
            </ul>,
            document.body
          )}
      </div>

      {error && (
        <div className="small text-danger mt-1">
          検索候補の取得に失敗しました
        </div>
      )}
    </>
  );
};

export default NameSearch;
